import React, { useState, useEffect } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import { User, Mail, Phone, Calculator, FileText, AlertCircle, CheckCircle, X, Loader, RefreshCw } from 'lucide-react';
import { testBackendHealth, testRiskProfileSubmission, testDatabaseConnection } from '../../utils/testBackendConnection';

const RiskProfilePage = () => {
  // API Configuration
  const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });

  // Questions and form state
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [questionnaireForms, setQuestionnaireForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });

  // Risk profiles based on score ranges
  const riskProfiles = {
    secure: {
      name: "Secure",
      scoreRange: "1-10",
      allocation: "100% Income",
      description: "This portfolio focuses entirely on the preservation of capital. As such the return is likely to be low and consistent compared with the other risk options offered. The portfolio is restricted in its ability to reduce taxable income or the tax effectiveness of that income. It is not an appropriate investment option for the medium to long-term investor seeking capital growth."
    },
    defensive: {
      name: "Defensive", 
      scoreRange: "11-15",
      allocation: "60% Income, 20% Growth",
      description: "This is an income focused portfolio that has a small exposure to growth assets. The main emphasis is on generating income, with some capital risk in order to achieve overall portfolio protection. It is expected to have a low fluctuation in short-term value, with some small shorter-term capital risk. The income generated by the portfolio may have a small tax efficient component share or property-based financing credits. It is suited to the investor who either seeks a high level of income or has a relatively short investment time frame."
    },
    conservative: {
      name: "Conservative",
      scoreRange: "16-20", 
      allocation: "60% Income, 40% Growth",
      description: "For investors who are seeking an income stream with some capital growth attached to it, this portfolio provides a mix of income assets but also includes exposure to share and property markets. It is suited to medium-term investors who are seeking a reasonable degree of capital stability. It is unlikely to protect their assets from inflation. Some tax relief on income may be available from franking credits."
    },
    balanced: {
      name: "Balanced",
      scoreRange: "21-30",
      allocation: "40% Income, 60% Growth", 
      description: "Using a slightly higher exposure to growth assets than income assets, this portfolio is expected to have longer short term fluctuations in value than the other growth-based investment portfolios. It is able to produce capital growth in a medium to long term time frame. It has a 'balanced' exposure to shares, property and fixed income assets, while the income generated by the portfolio may be partially tax effective."
    },
    growth: {
      name: "Growth",
      scoreRange: "31-34",
      allocation: "20% Income, 80% Growth",
      description: "A growth-oriented portfolio that is best suited to long-term investors. A small income exposure should slightly reduce the shorter-term fluctuations of the portfolio's value. It is best suited to long-term investors who can accept some investment risk over the long-run. The income stream may be partially tax effective and the portfolio has a high exposure to share and property to provide long-term investment growth."
    },
    highGrowth: {
      name: "High Growth",
      scoreRange: "35 or more",
      allocation: "100% Growth",
      description: "A 100% growth based portfolio with no exposure to income assets. It has a strong emphasis on maximising capital growth over the long term. The portfolio is likely to produce a higher but less effective income. Investors should expect high short-term fluctuations in values and a higher chance of capital loss. However, they are prepared to accept this as a trade off in achieving their long-term investment objective."
    }
  };

  const fetchQuestionnaireForms = async () => {
    try {
      console.log('Fetching questionnaire forms from:', `${API_BASE_URL}/questionnaire-forms`);
      
      const response = await fetch(`${API_BASE_URL}/questionnaire-forms`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Questionnaire forms fetched:', data);
      
      // Handle different response formats
      let formsData = [];
      if (data.success && Array.isArray(data.data)) {
        formsData = data.data;
      } else if (Array.isArray(data)) {
        formsData = data;
      }
      
      setQuestionnaireForms(formsData);
      
      // Auto-select the first active form if available
      const activeForm = formsData.find(form => form.isActive) || formsData[0];
      if (activeForm) {
        console.log('Auto-selecting form:', activeForm);
        setSelectedFormId(activeForm.id);
        await fetchQuestions(activeForm.id);
      } else {
        // Fallback to default questions
        await fetchDefaultQuestions();
      }
      
    } catch (error) {
      console.error('Error fetching questionnaire forms:', error);
      showMessage('Failed to load questionnaire forms. Loading default questions.', 'warning');
      // Fallback to default questions
      await fetchDefaultQuestions();
    }
  };

  // FIXED: fetchQuestions function with better error handling
   const fetchQuestions = async (formId) => {
    try {
      setQuestionsLoading(true);
      console.log('Fetching questions for form ID:', formId);
      
      // Test if the form exists first
      const testResponse = await fetch(`${API_BASE_URL}/questionnaire-forms/${formId}/info`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
        }
      });
      if (!testResponse.ok) {
        throw new Error(`Form not found or API not responding: ${testResponse.status}`);
      }
      
      const formInfo = await testResponse.json();
      console.log('Form info response:', formInfo);
      
      if (!formInfo.success) {
        throw new Error('Form not found');
      }
      
      // Now fetch the questions
      const response = await fetch(`${API_BASE_URL}/questionnaire-forms/${formId}/questions-list`, {
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
        }
      });
      
      if (!response.ok) {
        console.error('Questions endpoint failed with status:', response.status);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Questions response:', data);
      
      if (data.success && Array.isArray(data.data)) {
        if (data.data.length === 0) {
          showMessage(`Form "${data.formName}" has no questions available.`, 'warning');
          setQuestions([]);
          return;
        }
        
        // Questions now have proper ordering (1, 2, 3, etc.)
        const formattedQuestions = data.data.map((question, index) => ({
          id: question.id || (index + 1), // Use question.id (which is questionOrder) or fallback to index + 1
          text: question.text,
          options: question.options || []
        }));
        
        setQuestions(formattedQuestions);
        setAnswers({}); // Clear existing answers
        
        showMessage(`Loaded ${formattedQuestions.length} questions from "${data.formName}"`, 'success');
      } else {
        throw new Error('Invalid response format from server');
      }
      
    } catch (error) {
      console.error('Error fetching questions:', error);
      showMessage(`Failed to load questions: ${error.message}`, 'error');
      // No fallback - only show data from database or error
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Load default questions - removed, no fallback data
  const loadDefaultQuestions = async () => {
    console.log('Default questions loading disabled - only database data allowed');
    setQuestions([]);
    showMessage('Questions must be loaded from database. No cached data allowed.', 'warning');
  };

  // Fetch default questions (for initial load when no forms available)
  const fetchDefaultQuestions = async () => {
    try {
      setQuestionsLoading(true);
      console.log('Attempting to fetch default questions from API...');
      
      // Try to fetch from API first (though it doesn't exist yet)
      try {
        const response = await fetch(`${API_BASE_URL}/risk-profile/questions`, {
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
          }
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Default questions fetched from API:', data);
          
          let questionsData = [];
          if (data.success && Array.isArray(data.data)) {
            questionsData = data.data;
          } else if (Array.isArray(data)) {
            questionsData = data;
          }
          
          if (questionsData.length > 0) {
            setQuestions(questionsData);
            return;
          }
        }
      } catch (apiError) {
        console.log('API endpoint not available');
        setQuestions([]);
        showMessage('Risk profile questions are not available at this time.', 'warning');
      }
      
    } catch (error) {
      console.error('Error fetching default questions:', error);
      setQuestions([]);
      showMessage('Failed to load questions from database.', 'error');
    } finally {
      setQuestionsLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      try {
        await fetchQuestionnaireForms();
      } catch (error) {
        console.error('Error initializing data:', error);
        showMessage('Failed to initialize questionnaire. Please try refreshing.', 'error');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Handle form selection change
  const handleFormChange = async (formId) => {
    if (formId === selectedFormId) return;
    
    setSelectedFormId(formId);
    if (formId) {
      await fetchQuestions(formId);
    } else {
      await fetchDefaultQuestions();
    }
  };

  // Calculate risk profile based on total score
  const calculateRiskProfile = () => {
    const totalScore = Object.values(answers).reduce((sum, answer) => sum + answer, 0);
    
    if (totalScore >= 1 && totalScore <= 10) return riskProfiles.secure;
    if (totalScore >= 11 && totalScore <= 15) return riskProfiles.defensive;
    if (totalScore >= 16 && totalScore <= 20) return riskProfiles.conservative;
    if (totalScore >= 21 && totalScore <= 30) return riskProfiles.balanced;
    if (totalScore >= 31 && totalScore <= 34) return riskProfiles.growth;
    if (totalScore >= 35) return riskProfiles.highGrowth;
    
    return riskProfiles.balanced; // default
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Email is invalid';
    
    // Check if all questions are answered
    const unansweredQuestions = questions.filter(q => !answers[q.id]);
    if (unansweredQuestions.length > 0) {
      newErrors.questions = `Please answer all questions (${unansweredQuestions.length} remaining)`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showMessage('Please complete all required fields', 'error');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const totalScore = Object.values(answers).reduce((sum, answer) => sum + answer, 0);
      const profile = calculateRiskProfile();
      
      // Prepare submission data
      const submissionData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        formId: selectedFormId,
        answers: answers
        // Remove totalScore and riskProfile as they should be calculated by the backend
      };
      
      console.log('Submitting questionnaire:', submissionData);
      
      // Try to submit to API
      try {
        console.log('Submitting to API:', `${API_BASE_URL}/risk-profile/submit`);
        const response = await fetch(`${API_BASE_URL}/risk-profile/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
          },
          body: JSON.stringify(submissionData),
        });

        console.log('Response status:', response.status);
        
        if (response.ok) {
          const result = await response.json();
          console.log('Submission result:', result);
          setShowResult(true);
          showMessage('Risk profile assessment completed successfully!', 'success');
          
          // Reset form data after successful submission
          setFormData({
            firstName: '',
            lastName: '',
            phone: '',
            email: ''
          });
          setAnswers({});
          
          return;
        } else {
          const errorText = await response.text();
          console.error('API submission failed:', response.status, errorText);
          showMessage(`Server error: ${response.status} - ${errorText || 'Unknown error'}`, 'error');
        }
      } catch (apiError) {
        console.error('API not available:', apiError.message);
        showMessage(`Connection error: ${apiError.message}`, 'error');
      }
      
      // If API fails, still show the result to user
      setShowResult(true);
      showMessage('Risk profile assessment completed! (Note: Results not saved to server)', 'warning');
      
    } catch (error) {
      console.error('Submit error:', error);
      // Still show result even if there's an error
      setShowResult(true);
      showMessage('Risk profile calculated locally. Unable to save to server.', 'warning');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Handle answer selection (UPDATED TO WORK WITH CUSTOM FORMS)
  const handleAnswerChange = (questionId, optionIndex) => {
    // For custom forms, we need to get the actual score from the option
    const question = questions.find(q => q.id === questionId);
    let score = optionIndex + 1; // Default 1-based scoring
    
    if (question && question.options && question.options[optionIndex]) {
      const option = question.options[optionIndex];
      // If option has a score property, use it; otherwise use 1-based index
      score = option.score || (optionIndex + 1);
    }
    
    setAnswers(prev => ({ ...prev, [questionId]: score }));
    
    if (errors.questions) {
      setErrors(prev => ({ ...prev, questions: '' }));
    }
  };

  // Show message
  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Calculate current score
  const calculateCurrentScore = () => {
    return Object.values(answers).reduce((sum, answer) => sum + answer, 0);
  };

  // Show loading screen
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem',
          color: '#666'
        }}>
          <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
          <span>Loading questionnaire...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        body {
          margin: 0;
          padding: 0;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          background: repeating-linear-gradient(
            45deg,
            rgba(76, 175, 80, 0.05),
            rgba(76, 175, 80, 0.05) 10px,
            rgba(76, 175, 80, 0.02) 10px,
            rgba(76, 175, 80, 0.02) 20px
          );
          min-height: 100vh;
        }
        
        .radio-option:hover {
          background-color: #f9f9f9;
        }
        
        .radio-option.selected {
          background-color: #e8f5e8;
        }
        
        .submit-button:hover:not(:disabled) {
          background-color: #45a049;
        }
        
        .submit-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        input:focus, select:focus, textarea:focus {
          outline: none;
          border-color: #4CAF50;
          box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.2);
        }
        
        @media (max-width: 768px) {
          .name-fields {
            flex-direction: column !important;
          }
          
          .form-container {
            margin: 0 1rem !important;
            padding: 1.5rem !important;
          }
        }
      `}</style>

      <Header />

      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        {/* Message Notification */}
        {message.text && (
          <div style={{
            position: 'fixed',
            top: '1rem',
            right: '1rem',
            zIndex: 1000,
            padding: '1rem 1.5rem',
            borderRadius: '8px',
            fontWeight: '500',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            maxWidth: '400px',
            backgroundColor: message.type === 'success' ? '#d4edda' : 
                           message.type === 'warning' ? '#fff3cd' : 
                           message.type === 'info' ? '#d1ecf1' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : 
                   message.type === 'warning' ? '#856404' : 
                   message.type === 'info' ? '#0c5460' : '#721c24',
            border: `1px solid ${message.type === 'success' ? '#c3e6cb' : 
                                 message.type === 'warning' ? '#ffeaa7' : 
                                 message.type === 'info' ? '#bee5eb' : '#f5c6cb'}`
          }}>
            {message.type === 'success' ? (
              <CheckCircle size={20} />
            ) : (
              <AlertCircle size={20} />
            )}
            <span>{message.text}</span>
            <button
              onClick={() => setMessage({ text: '', type: '' })}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                marginLeft: '0.5rem'
              }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        {/* Result Modal */}
        {showResult && (
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            zIndex: 50,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem'
          }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '600px',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
            }}>
              <div style={{
                padding: '1.5rem',
                borderBottom: '1px solid #e5e7eb',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: '#4CAF50',
                color: 'white',
                borderRadius: '12px 12px 0 0'
              }}>
                <h3 style={{ fontSize: '1.5rem', fontWeight: '600', margin: 0 }}>
                  Risk Profile Assessment Result
                </h3>
                <button
                  onClick={() => setShowResult(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '0.5rem',
                    color: 'white'
                  }}
                >
                  <X size={24} />
                </button>
              </div>
              <div style={{ padding: '2rem' }}>
                {(() => {
                  const totalScore = Object.values(answers).reduce((sum, answer) => sum + answer, 0);
                  const profile = calculateRiskProfile();
                  
                  return (
                    <div>
                      <div style={{
                        textAlign: 'center',
                        marginBottom: '2rem',
                        padding: '1.5rem',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        border: '1px solid #4CAF50'
                      }}>
                        <h4 style={{
                          fontSize: '2rem',
                          fontWeight: 'bold',
                          color: '#2e7d32',
                          margin: '0 0 0.5rem 0'
                        }}>
                          {profile.name}
                        </h4>
                        <p style={{
                          fontSize: '1.125rem',
                          color: '#388e3c',
                          margin: '0 0 0.5rem 0',
                          fontWeight: '600'
                        }}>
                          {profile.allocation}
                        </p>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#2e7d32',
                          margin: 0
                        }}>
                          Total Score: {totalScore} (Range: {profile.scoreRange})
                        </p>
                      </div>
                      
                      <div style={{
                        padding: '1.5rem',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        marginBottom: '1.5rem'
                      }}>
                        <h5 style={{
                          fontSize: '1.125rem',
                          fontWeight: '600',
                          color: '#1a1a1a',
                          marginBottom: '1rem'
                        }}>
                          Profile Description
                        </h5>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#374151',
                          lineHeight: 1.6,
                          margin: 0
                        }}>
                          {profile.description}
                        </p>
                      </div>
                      
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#e3f2fd',
                        borderRadius: '8px',
                        border: '1px solid #90caf9'
                      }}>
                        <p style={{
                          fontSize: '0.875rem',
                          color: '#1565c0',
                          margin: 0,
                          textAlign: 'center',
                          fontWeight: '500'
                        }}>
                          <strong>Thank you {formData.firstName}!</strong><br />
                          Your personalized investment recommendation has been generated based on your responses.
                        </p>
                        
                        <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* Main Form Container */}
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '2rem 1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0'
          }} className="form-container">
            
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
              padding: '2rem',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h1 style={{
                margin: 0,
                fontSize: '1.5rem',
                fontWeight: '600'
              }}>
                Risk Profile Questionnaire
              </h1>
              <div style={{
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 'bold'
              }}>
                WinNest
              </div>
            </div>

            {/* Form Content */}
            <div style={{ padding: '2rem' }}>
              <form onSubmit={handleSubmit}>
                {/* Personal Information Section */}
                <div style={{ marginBottom: '2rem' }}>
                  {/* Name Field */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '0.5rem'
                    }}>
                      Name <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    <div style={{
                      display: 'flex',
                      gap: '1rem',
                      flexWrap: 'wrap'
                    }} className="name-fields">
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) => handleInputChange('firstName', e.target.value)}
                          placeholder="First Name"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: errors.firstName ? '1px solid #e74c3c' : '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem',
                            boxSizing: 'border-box'
                          }}
                        />
                        {errors.firstName && (
                          <div style={{
                            color: '#e74c3c',
                            fontSize: '0.75rem',
                            marginTop: '0.25rem'
                          }}>
                            {errors.firstName}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                          placeholder="Last Name"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: errors.lastName ? '1px solid #e74c3c' : '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '1rem',
                            boxSizing: 'border-box'
                          }}
                        />
                        {errors.lastName && (
                          <div style={{
                            color: '#e74c3c',
                            fontSize: '0.75rem',
                            marginTop: '0.25rem'
                          }}>
                            {errors.lastName}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Phone Field */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '0.5rem'
                    }}>
                      Phone <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '0.75rem',
                        border: errors.phone ? '1px solid #e74c3c' : '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                    />
                    {errors.phone && (
                      <div style={{
                        color: '#e74c3c',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem'
                      }}>
                        {errors.phone}
                      </div>
                    )}
                  </div>

                  {/* Email Field */}
                  <div style={{ marginBottom: '2rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.9rem',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '0.5rem'
                    }}>
                      Email <span style={{ color: '#e74c3c' }}>*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      style={{
                        width: '100%',
                        maxWidth: '400px',
                        padding: '0.75rem',
                        border: errors.email ? '1px solid #e74c3c' : '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '1rem',
                        boxSizing: 'border-box'
                      }}
                    />
                    {errors.email && (
                      <div style={{
                        color: '#e74c3c',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem'
                      }}>
                        {errors.email}
                      </div>
                    )}
                  </div>
                </div>

                {/* Questions Section */}
                {questions.length > 0 ? (
                  <div style={{ marginBottom: '2rem' }}>
                    {errors.questions && (
                      <div style={{
                        color: '#e74c3c',
                        fontSize: '0.875rem',
                        marginBottom: '1rem',
                        padding: '1rem',
                        backgroundColor: '#ffeaea',
                        borderRadius: '4px',
                        border: '1px solid #e74c3c'
                      }}>
                        <AlertCircle size={16} style={{ marginRight: '0.5rem' }} />
                        {errors.questions}
                      </div>
                    )}

                    {questionsLoading ? (
                      <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '2rem',
                        flexDirection: 'column',
                        gap: '1rem',
                        color: '#666'
                      }}>
                        <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                        <span>Loading questions...</span>
                      </div>
                    ) : (
                      questions.map((question, index) => (
                        <div key={question.id} style={{
                          marginBottom: '2rem',
                          paddingBottom: '1.5rem',
                          borderBottom: index < questions.length - 1 ? '1px solid #eee' : 'none'
                        }}>
                          <h3 style={{
                            fontSize: '1rem',
                            fontWeight: '500',
                            color: '#333',
                            marginBottom: '1rem',
                            lineHeight: 1.4
                          }}>
                            {question.id}. {question.text} <span style={{ color: '#e74c3c' }}>*</span>
                          </h3>
                          
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.5rem'
                          }}>
                            {question.options && question.options.map((option, optionIndex) => {
                              const isSelected = answers[question.id] === (
                                typeof option === 'object' && option.score ? option.score : optionIndex + 1
                              );
                              
                              return (
                                <label
                                  key={optionIndex}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '0.75rem',
                                    padding: '0.75rem',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#f0f8f0' : 'white',
                                    transition: 'all 0.2s ease'
                                  }}
                                  className={`radio-option ${isSelected ? 'selected' : ''}`}
                                >
                                  <input
                                    type="radio"
                                    name={`question-${question.id}`}
                                    checked={isSelected}
                                    onChange={() => handleAnswerChange(question.id, optionIndex)}
                                    style={{
                                      marginTop: '0.1rem',
                                      flexShrink: 0
                                    }}
                                  />
                                  <span style={{
                                    fontSize: '0.9rem',
                                    color: '#333',
                                    lineHeight: 1.4
                                  }}>
                                    {typeof option === 'string' ? option : option.text || option}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '2rem',
                    color: '#666'
                  }}>
                    <FileText size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                    <h3 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>No Questions Available</h3>
                    <p style={{ margin: 0 }}>
                      Please try refreshing the page or contact support if this issue persists.
                    </p>
                  </div>
                )}

                {/* Submit Button */}
                {questions.length > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '1rem'
                  }}>
                    <button
                      type="submit"
                      disabled={submitting || questionsLoading}
                      style={{
                        backgroundColor: submitting || questionsLoading ? '#ccc' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        padding: '1rem 2rem',
                        borderRadius: '4px',
                        fontSize: '1rem',
                        fontWeight: '600',
                        cursor: submitting || questionsLoading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        minWidth: '250px',
                        justifyContent: 'center',
                        transition: 'background-color 0.2s ease'
                      }}
                      className="submit-button"
                    >
                      {submitting ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid transparent',
                            borderTop: '2px solid white',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Processing...
                        </>
                      ) : (
                        <>
                          Submit to know your Risk Profile
                        </>
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default RiskProfilePage;